import { Editor, Range, Transforms } from 'slate';
import { insertLink } from './insertLink.mjs';
import { updateLink } from './updateLink.mjs';
import { unwrapLinks } from './unwrapLinks.mjs';
import { findLinks } from '../queries/findLink.mjs';
import { getClosestWordRange } from '../../../../queries/getClosestWordRange.mjs';
import { replaceRangeWithText } from '../../../../transforms/replaceRangeWithText.mjs';

/**
 * Unwrap link at a location (default: selection).
 * Then, the focus of the location is set to selection focus.
 * Then, wrap the link at the location.
 *
 * If there is no location provided, insert a new link at
 * the end of the document.
 */
function upsertLink(editor, {
  at = editor.selection,
  text,
  data
}) {
  if (!at) {
    insertLink(editor, data, null);
    return;
  }
  const links = findLinks(editor, at);

  // Update existing link if there is only one within the selection
  if (links.length === 1) {
    const [firstLinkEntry] = links;
    const [, firstLinkPath] = firstLinkEntry;
    const firstLinkRange = Editor.range(editor, firstLinkPath);
    const {
      anchor,
      focus
    } = at;
    if (Range.includes(firstLinkRange, anchor) && Range.includes(firstLinkRange, focus)) {
      updateLink(editor, {
        entry: firstLinkEntry,
        text,
        data
      });
      return;
    }
  }

  // Ensure the range is selected
  Transforms.select(editor, at);

  // Remove existing links within selection
  unwrapLinks(editor, at);

  // For subsequent transforms, we look at the
  // selection rather than the `at` range because
  // the `at` range is now out of date, whereas
  // the selection will get updated after a transform
  // is applied
  const selection = editor.selection;
  if (!selection) {
    return;
  }
  let range = Range.isCollapsed(selection) ? getClosestWordRange(editor, selection) : selection;
  if (text) {
    range = replaceRangeWithText(editor, {
      at: range,
      text
    });
  }
  insertLink(editor, data, range);

  // Collapse selection
  Transforms.collapse(editor, {
    edge: 'end'
  });
}

export { upsertLink };
